import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import serverStore from "../../../../stores/server.store";
import styled from "styled-components";
import {
    BoxedResponsiveStyled
} from "../../../../style/components/common.styled";
import ServerStatus from "../../../components/metrics/ServerStatus";
import ServerInfoWidget from "./serverWidgets/ServerInfoWidget";
import ServerMemoryWidget from "./serverWidgets/ServerMemoryWidget";
import ServerCpuWidget from "./serverWidgets/ServerCpuWidget";
import ServerNetworkWidget from "./serverWidgets/ServerNetworkWidget";
import ServerProcessesListWidget
    from "./serverWidgets/ServerProcessesListWidget";
import ServerProcessesWidget from "./serverWidgets/ServerProcessesWidget";
import ServerDisksWidget from "./serverWidgets/ServerDisksWidget";
import ServerNoDataRecieved from "./ServerNoDataRecieved";
import alarmsStore from "../../../../stores/alarms.store";
import AlarmsWidget from "./serverWidgets/AlarmsWidget";
import ServerItemChart from "./ServerItemChart";
import metricsStore from "../../../../stores/metrics.store";
import ServerLocationWidget from "./serverWidgets/ServerLocationWidget";
import ShowSpike from "./spikes/ShowSpike";
import { CURRENT_VERSION, spikesTypes } from "../../../../constants/data.types";
import { EuiIcon } from "@elastic/eui";
import ServerGpuWidget from "./serverWidgets/ServerGpuWidget";
import ServerAppsWidget from "./serverWidgets/ServerAppsWidget";
import ServerSshWidget from "./serverWidgets/ServerSshWidget";
import ModalUpdateAgent from "../../../components/modal/ModalUpdateAgent";
import utilsService from "../../../../servises/utils.service";
import ServerDrilldown from "./drilldowns/ServerDrilldown";
import CpuDrilldown from "./drilldowns/CpuDrilldown";
import DiskDrilldown from "./drilldowns/DiskDrilldown";
import ServerUsageSection from "./usage/ServerUsageSection";
import { calculateUsagePercent, isAgentUpdateAvailable } from "./serverSummary.utils";

const ServerItemWrapper = props => {
    return useObserver(() => (
        <>
            <ServerStatus
                server={ serverStore.serversListObj[props.serverId] }
            />
            <ServerItem
                processes={ serverStore.serverProcesses }
                server={ serverStore.serversListObj[props.serverId] }
                alarms={ alarmsStore.alarmsByServerObj[props.serverId] }
            />
            <ServerItemChart
                server={ serverStore.serversListObj[props.serverId] }
                cpu={ metricsStore.cpu }
                ram={ metricsStore.ram }
                net={ metricsStore.net }
                disk={ metricsStore.disk }
                disk_io={ metricsStore.disk_io }
                connections={ metricsStore.connections }
                processes={ metricsStore.processes }
                sessions={ metricsStore.sessions }
                file_handles={ metricsStore.file_handles }
                sinceSelected={ metricsStore.since }
                isLoading={ metricsStore.selectorLoading }
            />
            <ServerUsageSection
                serverId={ props.serverId }
                enabled={ !!serverStore.serversListObj[props.serverId]?.serverData?.agentPushedTheData }
            />
        </>
    ));
};

export default ServerItemWrapper;

export const BoxedResponsiveDashboardStyled = styled(BoxedResponsiveStyled)`
    margin: 0 auto 10px auto;

    &.network {
        //height: 662px;
    }

    h3 {
        position: relative;
        display: grid;
        align-items: flex-start;
        grid-template-columns: auto 1fr;
        grid-gap: 5px;
    }
`;

const HeaderControlsStyled = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SpikeWrapperStyled = styled.div`
  position: relative;
  width: 80px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  & > div {
    position: relative !important;
    right: auto !important;
    top: auto !important;
  }
`;

export const ServerNameStyled = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    margin: 10px 20px;
`;

const UpdateStyled = styled.div`
    color: rgb(249, 190, 73);
    cursor: pointer;
    font-size: 14px;
`;

const MiniHeadStyled = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
`;

const SummaryBarStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-gap: 10px;
  padding: 0 10px;
  margin-top: 10px;
  margin-bottom: 4px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCardStyled = styled.div`
  background: rgb(29, 30, 36);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: rgb(0 0 0 / 20%) 0px 1px 3px;
  transition: all 0.2s ease-in-out;
  min-width: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  .icon-wrapper {
    background: ${props => props.color || 'rgba(0, 128, 213, 0.1)'};
    color: ${props => props.textColor || 'rgb(0, 128, 213)'};
    padding: 8px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    width: 100%;
    
    .label {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.45);
      font-weight: 600;
      letter-spacing: 0.5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .value {
      font-size: 15px;
      font-weight: 700;
      color: #fff;
      font-family: Consolas, monospace;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const ServerDashboardStyled = styled.section`
    display: grid;
    grid-template-columns: 4fr 2fr;
    padding: 1px 10px;
    margin-top: 8px;
    grid-gap: 8px;

    @media (max-width: 1025px) {
        grid-template-columns: 1fr;
    }

    .left {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        grid-gap: 10px;

        @media (max-width: 1200px) {
            grid-template-columns: 1fr 1fr;
        }

        @media (max-width: 600px) {
            grid-template-columns: 1fr;
        }
    }

    .right {
        display: grid;
        grid-template-rows: 1fr 1fr;

        @media (max-width: 600px) {
            grid-template-columns: 1fr;
        }

        .column-body {
            //height: 326px;
        }
    }

    .column-body {
        padding: 10px;
    }

`;

export const ColumnStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ServerItem = props => {
    const history = useHistory();

    const [showModalUpdate, s_showModalUpdate] = useState(false);

    const { server, processes, alarms } = props;

    if (server) {
        document.title = `${ server.name } - SyAgent`;
    }

    const metric = server?.metrics?.[0];
    const isUpdateOfAgentAvailable = isAgentUpdateAvailable(metric, CURRENT_VERSION);
    const isGpuAvailable = !!(metric && metric.gpu_info && metric.gpu_info.length > 0 && metric.gpu_info[0].is_available);

    // Calculate core stats for summary bar
    const cpuLoad = utilsService.getFromMetrics(server, 'load_cpu', 0);
    
    const ramPercent = calculateUsagePercent(metric, 'ram_usage', 'ram_total');
    const diskPercent = calculateUsagePercent(metric, 'disk_usage', 'disk_total');

    const uptimeSec = utilsService.getFromMetrics(server, 'uptime', 0);
    const formatUptimeShort = (seconds) => {
        const days = Math.floor(seconds / (24 * 60 * 60));
        const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((seconds % (60 * 60)) / 60);
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };
    const uptimeFormatted = formatUptimeShort(uptimeSec);

    // Calculate top CPU and memory processes
    let topCpuProcess = null;
    let topRamProcess = null;

    if (processes && processes.processes_list && processes.processes_list.length > 0) {
        let maxCpu = -1;
        let maxRam = -1;
        processes.processes_list.forEach(p => {
            const cpuVal = parseFloat(p.cpu) || 0;
            if (cpuVal > maxCpu) {
                maxCpu = cpuVal;
                topCpuProcess = p;
            }
            
            let ramVal = p.ramBytes;
            if (ramVal === undefined) {
                ramVal = parseFloat(p.ram) || 0;
            }
            if (ramVal > maxRam) {
                maxRam = ramVal;
                topRamProcess = p;
            }
        });
    }

    const renderTopCpuProcess = () => {
        if (!topCpuProcess) return <span className="value">N/A</span>;
        return (
            <div className="value" title={`${topCpuProcess.process_name} (${parseFloat(topCpuProcess.cpu)}%)`}>
                { topCpuProcess.process_name }
                <span style={{ fontSize: '11px', color: '#f86b63', marginLeft: '4px', fontWeight: 600 }}>
                    ({ parseFloat(topCpuProcess.cpu) }%)
                </span>
            </div>
        );
    };

    const renderTopRamProcess = () => {
        if (!topRamProcess) return <span className="value">N/A</span>;
        return (
            <div className="value" title={`${topRamProcess.process_name} (${topRamProcess.ram})`}>
                { topRamProcess.process_name }
                <span style={{ fontSize: '11px', color: '#3b82f6', marginLeft: '4px', fontWeight: 600 }}>
                    ({ topRamProcess.ram })
                </span>
            </div>
        );
    };

    return (
        <>
            { server && (
                <MiniHeadStyled>
                    <ServerNameStyled>
                        { server.name }
                        { server.serverData && server.serverData.timeAgo && (
                            <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 500, marginLeft: '12px' }}>
                                Updated { server.serverData.timeAgo }
                            </span>
                        ) }
                    </ServerNameStyled>
                    { isUpdateOfAgentAvailable && (
                        <UpdateStyled onClick={ () => s_showModalUpdate(true) }>
                            <EuiIcon type="alert"/> Update of Agent available
                        </UpdateStyled>
                    ) }
                </MiniHeadStyled>
            ) }
            { server && server.serverData.agentPushedTheData && (
                <SummaryBarStyled>
                    <SummaryCardStyled color="rgba(159, 122, 234, 0.1)" textColor="#9f7aea">
                        <div className="icon-wrapper">
                            <EuiIcon type="compute" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">CPU LOAD</div>
                            <div className="value">{ cpuLoad }%</div>
                        </div>
                    </SummaryCardStyled>
                    <SummaryCardStyled color="rgba(0, 203, 214, 0.1)" textColor="#00cbd6">
                        <div className="icon-wrapper">
                            <EuiIcon type="memory" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">MEMORY USED</div>
                            <div className="value">{ ramPercent }%</div>
                        </div>
                    </SummaryCardStyled>
                    <SummaryCardStyled color="rgba(237, 137, 54, 0.1)" textColor="#ed8936">
                        <div className="icon-wrapper">
                            <EuiIcon type="storage" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">DISK USED</div>
                            <div className="value">{ diskPercent }%</div>
                        </div>
                    </SummaryCardStyled>
                    <SummaryCardStyled color="rgba(72, 187, 120, 0.1)" textColor="#48bb78">
                        <div className="icon-wrapper">
                            <EuiIcon type="clock" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">UPTIME</div>
                            <div className="value">{ uptimeFormatted }</div>
                        </div>
                    </SummaryCardStyled>
                    <SummaryCardStyled color="rgba(248, 107, 99, 0.1)" textColor="#f86b63">
                        <div className="icon-wrapper">
                            <EuiIcon type="createPopulationJob" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">TOP CPU PROCESS</div>
                            { renderTopCpuProcess() }
                        </div>
                    </SummaryCardStyled>
                    <SummaryCardStyled color="rgba(59, 130, 246, 0.1)" textColor="#3b82f6">
                        <div className="icon-wrapper">
                            <EuiIcon type="createPopulationJob" size="m"/>
                        </div>
                        <div className="info">
                            <div className="label">TOP MEM PROCESS</div>
                            { renderTopRamProcess() }
                        </div>
                    </SummaryCardStyled>
                </SummaryBarStyled>
            ) }
            <ServerDashboardStyled>
                { server && server.serverData.agentPushedTheData && (
                    <>
                        <div className="left">
                            <ColumnStyled className="column petra">
                                <BoxedResponsiveDashboardStyled
                                    className="column-body">
                                    <h3>
                                        <EuiIcon type="securitySignal" size="m"/> Server
                                        <HeaderControlsStyled>
                                            <ServerDrilldown server={ server } />
                                        </HeaderControlsStyled>
                                    </h3>
                                    <ServerInfoWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                                <BoxedResponsiveDashboardStyled
                                    className="column-body" style={{ minHeight: '272px' }}>
                                    <h3><EuiIcon type="memory" size="m"/> Memory
                                        <ShowSpike
                                            type={ spikesTypes.RAM }
                                            serverId={ server._id }
                                        />
                                    </h3>
                                    <ServerMemoryWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                            </ColumnStyled>
                            <ColumnStyled className="column huetra">
                                <BoxedResponsiveDashboardStyled
                                    className="column-body">
                                    <h3>
                                        <EuiIcon type="compute" size="m"/> Cpu
                                        <HeaderControlsStyled>
                                            <CpuDrilldown server={ server } />
                                            <SpikeWrapperStyled>
                                                <ShowSpike
                                                    type={ spikesTypes.CPU }
                                                    serverId={ server._id }
                                                />
                                            </SpikeWrapperStyled>
                                        </HeaderControlsStyled>
                                    </h3>
                                    <ServerCpuWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                                <BoxedResponsiveDashboardStyled
                                    className="column-body" style={{ minHeight: '387px' }}>
                                    <h3><EuiIcon type="kubernetesNode" size="m"/> Gpu</h3>
                                    <ServerGpuWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                            </ColumnStyled>
                            <ColumnStyled className="column petra">
                                <BoxedResponsiveDashboardStyled
                                    className="column-body network">
                                    <h3><EuiIcon type="globe" size="m"/> Network</h3>
                                    <ServerNetworkWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                                <BoxedResponsiveDashboardStyled
                                    className="column-body">
                                    <h3><EuiIcon type="mapMarker" size="m"/> SSH Security</h3>
                                    <ServerSshWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                                <BoxedResponsiveDashboardStyled
                                    className="column-body">
                                    <h3><EuiIcon type="apps" size="m"/> Software Stack</h3>
                                    <ServerAppsWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                            </ColumnStyled>
                            <ColumnStyled className="column petra">
                                <BoxedResponsiveDashboardStyled
                                    className="column-body">
                                    <h3>
                                        <EuiIcon type="storage" size="m"/> Disks
                                        <HeaderControlsStyled>
                                            <DiskDrilldown server={ server } />
                                            <SpikeWrapperStyled>
                                                <ShowSpike
                                                    type={ spikesTypes.DISK }
                                                    serverId={ server._id }
                                                />
                                            </SpikeWrapperStyled>
                                        </HeaderControlsStyled>
                                    </h3>
                                    <ServerDisksWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                                <BoxedResponsiveDashboardStyled
                                    className="column-body" style={{ minHeight: '269px' }}>
                                    <h3><EuiIcon type="createPopulationJob" size="m" color="text"/> Processes</h3>
                                    <ServerProcessesWidget
                                        server={ server }
                                    />
                                </BoxedResponsiveDashboardStyled>
                            </ColumnStyled>
                        </div>
                        <div className="right">
                            <BoxedResponsiveDashboardStyled
                                className="column-body">
                                <AlarmsWidget
                                    alarms={ alarms }
                                    server={ server }
                                />
                            </BoxedResponsiveDashboardStyled>
                            <BoxedResponsiveDashboardStyled
                                className="column-body">
                                <h3><EuiIcon type="createPopulationJob" size="m" color="text"/> Processes</h3>
                                <ServerProcessesListWidget
                                    server={ server }
                                    processes={ processes }
                                />
                            </BoxedResponsiveDashboardStyled>
                        </div>
                    </>
                ) }
            </ServerDashboardStyled>
            { server && !server.serverData.agentPushedTheData && (
                <ServerNoDataRecieved
                    server={ server }
                />
            ) }
            { showModalUpdate && (
                <ModalUpdateAgent
                    data={ {
                        server: server
                    } }
                    close={ () => s_showModalUpdate(false) }
                />
            ) }
        </>
    );
};
